import { FormFieldGenericErrorResponse } from "global/interfaces/http.interface";
import ObservableState from 'global/js/ObservableState';
import { MoleculeFormEvents } from 'wolf/components/molecules/m-form/m-form.events';
import { ResumeStepEvents } from 'wolf/components/molecules/m-formstep-resume-step/m-formstep-resume-step-controller';
import ControllerBase from '../../../../global/js/ControllerBase';
import {
	FormstepSelectImageEvents,
	MoleculeSelectImageOptionChangedEvent
} from "../../molecules/m-formstep-select-image/m-formstep-select-image.events";
import { FormStepValidationConfig, OrganismFormStepperProps } from './o-form-stepper.data';
import { FormStepperValidationEvent, FormStepperValidationEventClientServerEnum, OrganismFormStepperEvents, ValidationEventMapper } from './o-form-stepper.events';
import { FormStepperComponentRefs, FormStepperState, FormStepperStateStepValidation, FORM_STEPPER_CONFIG_ID, GenericFormStepValidationResponseWithSliceError, GenericFormStepValidationResponseWithSliceResponse, SubmitUserProfilingResponse, SubmitUserProfilingResponseFailed } from './o-form-stepper.types';
const alertTemplate = require('../../molecules/m-alert/m-alert.template.twig') as ({ data }) => string;

// dirty logging without env - turn off logging easily
const logs = false;

export default class ConrollerFormSteps extends ControllerBase<FormStepperComponentRefs> {
	private static readonly componentRefs: FormStepperComponentRefs = {
		formComponent: 'o-form-stepper__submit-form',
		switcher: 'o-form-stepper-switcher',
		sidebarWrapper: 'o-form-stepper-sidebar',
		nextStepButton: 'form-next-step-button',
		prevStepButton: 'form-prev-step-button',
		formSubmitButton: 'form-submit-button',
		bottomNavigation: 'o-form-stepper__bottom-navigation',
		topBackButton: 'o-form-stepper__top-back',
		topCloseButton: 'o-form-stepper__top-close',
		submissionAlert: 'o-form-stepper-submission-alert'
	};

	private formComponent: HTMLFormElement;
	private switcher: HTMLUListElement;
	private sidebarWrapper: HTMLDivElement;
	private sidebar: HTMLUListElement | null;
	private nextStepButton: HTMLButtonElement;
	private prevStepButton: HTMLButtonElement;
	private formSubmitButton: HTMLInputElement;
	private formConfig: {
		steps: OrganismFormStepperProps['formSteps'],
		settings: OrganismFormStepperProps['formSettings']
	};
	private bottomNavigation: HTMLDivElement;
	private submissionAlert: HTMLDivElement;
	private topBackButton: HTMLAnchorElement;
	private topCloseButton: HTMLAnchorElement;

	private state: ObservableState<FormStepperState>;

	private readonly _defaultState: FormStepperState = {
		totalSteps: 0,
		currentStep: 0,
		requestedStep: 0,
		currentDirection: 'next',
		validationConfig: [],
		validatedSteps: [],
		stepResumes: [],
		controlledForm: {},
		selectedOptions: []
	};

	constructor(element: HTMLElement) {
		super(element, [], Object.values(ConrollerFormSteps.componentRefs));

		this.formComponent = this.$references[ConrollerFormSteps.componentRefs.formComponent] as HTMLFormElement
		this.switcher = this.$references[ConrollerFormSteps.componentRefs.switcher] as HTMLUListElement;
		this.sidebarWrapper = this.$references[ConrollerFormSteps.componentRefs.sidebarWrapper] as HTMLDivElement;
		this.sidebar = this.sidebarWrapper ? this.sidebarWrapper.querySelector<'ul'>('ul') : null;
		this.nextStepButton = this.$references[ConrollerFormSteps.componentRefs.nextStepButton] as HTMLButtonElement
		this.prevStepButton = this.$references[ConrollerFormSteps.componentRefs.prevStepButton] as HTMLButtonElement
		this.formSubmitButton = this.$references[ConrollerFormSteps.componentRefs.formSubmitButton] as HTMLInputElement
		this.bottomNavigation = this.$references[ConrollerFormSteps.componentRefs.bottomNavigation] as HTMLDivElement
		this.topBackButton = this.$references[ConrollerFormSteps.componentRefs.topBackButton] as HTMLAnchorElement;
		this.topCloseButton = this.$references[ConrollerFormSteps.componentRefs.topCloseButton] as HTMLAnchorElement;
		this.submissionAlert = this.$references[ConrollerFormSteps.componentRefs.submissionAlert] as HTMLDivElement;

		this.state = new ObservableState<FormStepperState>(this._defaultState, {
			RUN_VALIDATIONS_ON_STEP_CHANGE: {
				callback: this.onValidateStepChange,
				// deps: ['currentStep']
				deps: ['requestedStep'],
				// alwaysTrigger: ['requestedStep'],
			},
			UPDATE_NAVIGATION_ITEMS: {
				callback: this.onUpdateNavigation,
				deps: ['validatedSteps', 'currentStep']
			}
		});
		this.initApp();
	}

	private goToStep = (): void => {
		const { requestedStep, currentDirection } = this.state.get()
		// show the new requested step
		this.showStep(requestedStep)
		logs && console.log('Going to step ', requestedStep);
		this.state.set(prevState => ({ ...prevState, currentStep: requestedStep }));

		const newState = this.state.get();

		const stepHasAScore = newState.validationConfig[newState.currentStep] !== undefined;
		if (stepHasAScore) {
			// check if step only has front end validation
			const onlyHasFrontendValidation = newState.validationConfig[newState.currentStep].validationScoreRequired === FormStepperValidationEventClientServerEnum.Frontend
			// if (currentDirection === 'next' && !onlyHasFrontendValidation) this.resetValidationScore(requestedStep)
		}

		logs && console.warn('New state ', this.state.get());
	}

	private onUpdateNavigation = (currentState: FormStepperState): void => {
		this.updateSidebarNavigationClickableItems(currentState)
		this.updateIconsAndActiveSidebarItem(currentState)
		this.manageStepButtonsState(currentState)
	}

	private manageStepButtonsState = (currentState: FormStepperState): void => {
		const valid = this.stepIsValid(currentState.currentStep);
		if (!valid) {
			this.domUtils.toggleElementState(this.nextStepButton, ['disable']);
		} else {
			this.domUtils.toggleElementState(this.nextStepButton, ['enable']);
		}

		if (currentState.currentStep === 0) {
			this.domUtils.toggleElementState(this.prevStepButton, ['disable']);
		} else {
			this.domUtils.toggleElementState(this.prevStepButton, ['show', 'enable']);
		}

		if (currentState.totalSteps === currentState.currentStep + 1) {
			this.domUtils.toggleElementState(this.formSubmitButton, ['show']);
			this.formSubmitButton.classList.remove('uk-hidden');
			this.domUtils.toggleElementState(this.nextStepButton, ['hide']);
		} else {
			this.domUtils.toggleElementState(this.formSubmitButton, ['hide']);
			this.formSubmitButton.classList.add('uk-hidden');
			this.domUtils.toggleElementState(this.nextStepButton, ['show']);
		}

		if (currentState.totalSteps === currentState.currentStep) {
			this.domUtils.toggleElementState(this.bottomNavigation, ['hide']);
		}
	}

	private updateSidebarNavigationClickableItems(currentState: FormStepperState): void {
		[...this.sidebar?.querySelectorAll<HTMLAnchorElement>('a') as NodeListOf<HTMLAnchorElement>]
			.forEach((item, i) => {
				const isFirstElement = item.dataset.step === '0'
				// const shouldEnableClickability = currentState.validatedSteps.includes(i) && currentState.validatedSteps.includes(i - 1)
				const shouldEnableClickability = this.stepIsValid(i) && this.stepIsValid(i - 1);
				if (isFirstElement || shouldEnableClickability) {
					item.setAttribute('style', 'pointer-events: auto')
				} else {
					item.setAttribute('style', 'pointer-events: none')
				}
			});
	}

	/**
	 * Returns true if the current step has met the validation score required to make the current step valid
	 * Takes into consideration validation from frontend and backend, or none at all
	 * @param  {FormStepperState['currentStep']} step
	 * @returns boolean
	 */
	private stepIsValid(step: FormStepperState['currentStep']): boolean {
		if (step < 0) return true; // support for checking steps which don't exist
		if (this.state.get().validatedSteps[step] === undefined) return true; // support for final step having no config
		return this.state.get().validatedSteps[step].validationScoreCurrent >= this.state.get().validatedSteps[step].validationScoreRequired;
	}

	/**
	 * Update this step's validity score by setting a new score
	 * @param  {boolean} valid
	 * @param  {FormStepperState['currentStep']} step
	 * @returns void
	 */
	private setStepValidity = (callbackValidationType: FormStepperValidationEventClientServerEnum, validity: 'valid' | 'invalid'): void => {
		// get current validation list
		const validatedSteps = this.state.get().validatedSteps;
		const { currentStep } = this.state.get();

		const config = this.getStepValidationConfig();
		// bitwise comparison to see if this validation type is required
		const isRequired = (config.validationScoreRequired & callbackValidationType) > 0;
		// see if score bit is set yet
		const isSet = (validatedSteps[currentStep].validationScoreCurrent & callbackValidationType) > 0;

		if (isRequired) {
			if (!isSet && validity === 'valid') {
				// add the validation type value to the current score for this step
				validatedSteps[currentStep].validationScoreCurrent += callbackValidationType;
			} else if (isSet && validity === 'invalid') {
				logs && console.warn('invalidate the score flag for value: ', { step: currentStep, type: callbackValidationType });
				// remove validation score
				validatedSteps[currentStep].validationScoreCurrent -= callbackValidationType;
			}
		}

		this.state.set((prevState) => ({ ...prevState, validatedSteps }));
	}

	private resetValidationScore(step: FormStepperState['currentStep']): void {
		const { validatedSteps } = this.state.get();
		validatedSteps[step].validationScoreCurrent = 0;
		this.state.set((prevState) => ({ ...prevState, validatedSteps }));
	}

	private getStepValidationConfig(step?: number): FormStepValidationConfig {
		if (step && step > -1) {
			return this.state.get().validationConfig[step];
		}

		const { currentStep } = this.state.get();

		return this.state.get().validationConfig[currentStep];
	}

	/**
	 * Get validation score for the current step
	 * steps need to achieve this score in order to pass validation and go to the next step
	 * score: 0 = no validation necessary
	 * score: 1 = requires frontend
	 * score: 2 = requires backend
	 * score: 3 = requires frontend and backend
	 * @param  {string|null} event
	 * @param  {string|null} url
	 * @returns FormStepValidationConfig
	 */
	private getStepValidationScoreRequired(event: string | null, url: string | null): FormStepValidationConfig['validationScoreRequired'] {

		let score = 0;
		if (event) {
			score += FormStepperValidationEventClientServerEnum.Frontend;
		}
		if (url) {
			score += FormStepperValidationEventClientServerEnum.Backend;
		}
		return score
	}

	private onValidationSuccess = (callbackValidationType?: FormStepperValidationEventClientServerEnum): void => {
		const { currentStep } = this.state.get();
		// const { validationScoreRequired } = this.getStepValidationConfig();

		const currentDirectionIsNext = this.state.get().currentDirection === 'next'

		// callbackValidationType && this.setStepValidity(callbackValidationType, 'valid', currentDirectionIsNext ? currentStep - 1 : currentStep);
		callbackValidationType && currentDirectionIsNext && this.setStepValidity(callbackValidationType, 'valid');
		// this.setCurrentStep(currentStep);
		const requestedStep = currentDirectionIsNext ? currentStep + 1 : currentStep - 1;
		this.setRequestedStep(requestedStep);

		// TODO - check this score before being allowed to go to the next step...
		if (this.stepIsValid(currentStep)) {
			this.goToStep();
		} else {
			logs && console.warn('Step not valid current score is: ', this.state.get().validatedSteps[currentStep])
		}
	}

	private onValidationError = (callbackValidationType?: FormStepperValidationEventClientServerEnum): void => {
		const currentDirectionIsNext = this.state.get().currentDirection === 'next'
		callbackValidationType && currentDirectionIsNext && this.setStepValidity(callbackValidationType, 'invalid');
		// update the requested step so the side effect will run when the user clicks next

		const { currentStep } = this.state.get();
		this.setRequestedStep(currentStep);
	}

	/**
	 * Build a form data payload for fields inside a specific step
	 * @returns FormData
	 */
	private getStepValidationData = (): { stepId: string, formSlice: Record<string, string>, formData: FormData } | undefined => {
		const { currentStep } = this.state.get();
		const stepLi = this.switcher.children[currentStep] as HTMLLIElement;
		if (!stepLi) return undefined;

		const fieldsetId = stepLi.querySelector('fieldset')?.id
		const formData = new FormData();
		const fields = Array.from(
			stepLi.querySelectorAll('input, textarea, select')
		);
		fields.forEach((field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement) => {
			// only append field if it has a name
			if (field.name) {
				switch (field.type.toLowerCase()) {
					case 'checkbox': {
						formData.append(field.name, (field as HTMLInputElement).checked === true ? 'true' : 'false');
						break;
					}
					default: {
						formData.append(field.name, field.value);
						break;
					}
				}

			}
		});
		const parsedFormFields: Record<string, string> = {};
		const formEntriesPairs: [string, FormDataEntryValue][] = [...formData.entries()];
		formEntriesPairs.forEach((element) => {
			const el = element as string[]
			parsedFormFields[el[0]] = el[1];
		});

		return { stepId: fieldsetId || '', formSlice: parsedFormFields, formData };
	}

	private runValidationViaUrl = async (): Promise<void> => {
		const { currentStep } = this.state.get();
		const { validationUrl } = this.getStepValidationConfig()

		if (validationUrl) {
			const stepValidationData = this.getStepValidationData();
			if (stepValidationData) {
				const { stepId, formSlice, formData } = stepValidationData;
				this.performValidationRequest<GenericFormStepValidationResponseWithSliceResponse>(validationUrl, formData)
					.then(resp => {
						// HTTP 200
						if (resp.success === true && resp.data) {
							this.onValidationSuccess(FormStepperValidationEventClientServerEnum.Backend)
							const resumeSlices = resp.data ? [resp.data.resumeSliceMolecule] : [];
							this.dispatchCustomEvent(
								OrganismFormStepperEvents.RECEIVE_RESUME_SLICE,
								{
									step: currentStep,
									resumeSlices
								},
								true,
								true
							)
							this.dispatchCustomEvent(OrganismFormStepperEvents.RECEIVE_CONTROLLED_FORM_SLICE, { stepId, formSlice }, false, true)
						} else {
							// if (resp.error === true && resp.message) {
							logs && console.warn('Some error occured with the server');
							this.onValidationError(FormStepperValidationEventClientServerEnum.Backend)

							this.dispatchCustomEvent(
								MoleculeFormEvents.DISPLAY_VALIDATION_ERRORS,
								(resp as GenericFormStepValidationResponseWithSliceError).error,
								false,
								true,
							);

						}

					})
					.catch((err: FormFieldGenericErrorResponse) => {// if (err.error === true && err.message)
						// !== HTTP 200
						this.domUtils.toggleElementState(this.nextStepButton, ['disable']);
						this.onValidationError(FormStepperValidationEventClientServerEnum.Backend)

					});

			}
		}
	}

	private runValidationsViaEvent = async (): Promise<void> => {
		const { validationEvent, validationUrl } = this.getStepValidationConfig()

		if (validationEvent) {
			const { currentStep } = this.state.get();
			const eventDetail: FormStepperValidationEvent = {

				onValidationSuccess: this.onValidationSuccess,
				// onValidationSuccess: () => this.onValidationSuccess(FormStepperValidationEventClientServerEnum.Backend),

				onValidationError: () => {
					logs && console.warn('Todo...Some error occured during validation')
					this.onValidationError(FormStepperValidationEventClientServerEnum.Backend)
				},
				currentStep: currentStep
			};

			this.dispatchCustomEvent<FormStepperValidationEvent>(
				validationEvent,
				eventDetail,
				false,
				true,
			);

			if (validationUrl) this.runValidationViaUrl()
		}
	}

	private performValidationRequest = async <T>(validationUrl: string, body?: FormData): Promise<T> => {
		const url = new URL(validationUrl);
		return await this.validateStep<
			T
		>(url, {
			body: body,
		});
	}

	/** Side-effect function triggered when a new step is requested */
	private onValidateStepChange = (): void => {
		const { currentDirection, requestedStep, currentStep, totalSteps } = this.state.get();
		// only run if requested step is valid
		if (requestedStep < 0) {
			// this.resetValidationScore(currentStep);
			logs && console.warn('Requested step is now: ', requestedStep)
			return;
		}

		const { validationEvent, validationUrl } = this.getStepValidationConfig()
		const hasNoValidation = !validationEvent && !validationUrl
		logs && console.warn({ hasNoValidation, validationEvent, validationUrl, state: this.state.get() })

		// the final submission doesn't require step validation, just show the finishing screen
		if (requestedStep === totalSteps || hasNoValidation) {
			// go to confirmation screen
			this.goToStep();
			return;
		}

		if (currentDirection === 'prev') {
			this.goToStep();
			return;
		}

		if (validationEvent) {
			this.runValidationsViaEvent()
		} else if (validationUrl) {
			this.runValidationViaUrl()
		}


	}

	private setCurrentStep = (currentStep: FormStepperState['currentStep']): void => {
		this.state.set((prevState) => ({ ...prevState, currentStep }))
	}

	private setCurrentDirection = (currentDirection: FormStepperState['currentDirection']): void => {
		this.state.set((prevState) => ({ ...prevState, currentDirection }))
	}

	private calculateTotalSteps(): void {
		this.state.set((prevState) => ({ ...prevState, totalSteps: this.formConfig.steps.length }));
	}

	private setRequestedStep(directionOrStepNumber: 'next' | 'prev' | number): void {
		const { currentStep } = this.state.get();
		let newStep: FormStepperState['requestedStep'];

		if (directionOrStepNumber === 'next') {
			newStep = currentStep + 1
		} else if (directionOrStepNumber === 'prev') {
			newStep = currentStep - 1 > 0 ? currentStep - 1 : 0
		} else {
			newStep = directionOrStepNumber
		}
		const direction = newStep > currentStep ? 'next' : 'prev';
		// set direction first
		this.setCurrentDirection(direction);
		// trigger an effect
		this.setRequestedNextStep(newStep)
		// this.setCurrentStep(newStep);
	}

	private setRequestedNextStep(requestedStep: FormStepperState['requestedStep']): void {
		this.state.set((prevState) => ({ ...prevState, requestedStep }));
	}

	private observeStepsHandler(): void {
		this.inputUtils.observeElementEvents(this.nextStepButton, 'click', () => this.setRequestedStep('next'));
		this.inputUtils.observeElementEvents(this.prevStepButton, 'click', () => this.setRequestedStep('prev'));
	}

	private addResumeSliceToSummary = (event: CustomEvent): void => {
		const stepResumesStateUpdated = this.state.get().stepResumes
		const newResumeSlice = event.detail
		const shouldUpdateResumeStep = stepResumesStateUpdated.map(({ step }) => step).some((step) => step === newResumeSlice.step)
		if (shouldUpdateResumeStep) {
			const indexSliceToReplace = stepResumesStateUpdated.findIndex(({ step }) => step === newResumeSlice.step)
			stepResumesStateUpdated[indexSliceToReplace] = newResumeSlice
		} else stepResumesStateUpdated.push(newResumeSlice)

		this.state.set((prevState) => ({ ...prevState, stepResumes: stepResumesStateUpdated }));
		this.dispatchCustomEvent(ResumeStepEvents.RECEIVE_RESUMES_SLICE_SUMMARY, { stepResumes: stepResumesStateUpdated.map(({ resumeSlices }) => resumeSlices) }, false, true)
	}

	private addSliceToControlledForm = (event: CustomEvent): void => {
		const controlledFormUpdated = this.state.get().controlledForm
		const newControlledFormSlice = event.detail
		controlledFormUpdated[newControlledFormSlice.stepId] = newControlledFormSlice
		this.state.set((prevState) => ({ ...prevState, controlledForm: controlledFormUpdated }));
	}

	private removeSliceFromControlledForm = (event: CustomEvent): void => {
		const controlledFormUpdated = this.state.get().controlledForm
		const newControlledFormSlice = event.detail
		delete controlledFormUpdated[newControlledFormSlice.stepId];

		this.state.set((prevState) => ({ ...prevState, controlledForm: controlledFormUpdated }));
	}

	private formDataChangedByStep = (event: CustomEvent): void => {
		const { currentStep } = this.state.get();
		this.resetValidationScore(currentStep);

		logs && console.warn('FormDataChangedByStep ', this.state.get());
	}

	private selectedImageOptionsChanged = (event: CustomEvent): void => {
		const { selectedOptions } = this.state.get();
		const selectedValue = event.detail.selectedValue;
		const isSelected = event.detail.isSelected;

		// add or remove selectedValue to the state
		if (isSelected) {
			selectedOptions.push(selectedValue);
		} else {
			const indexToDelete = selectedOptions.indexOf(selectedValue, 0);
			if (indexToDelete > -1) {
				selectedOptions.splice(indexToDelete, 1);
			}
		}

		// save selectedOptions back to state
		this.state.set((prevState) => ({ ...prevState, selectedOptions: selectedOptions }));

		this.dispatchCustomEvent<MoleculeSelectImageOptionChangedEvent>(FormstepSelectImageEvents.SELECT_OPTION_CHANGED, {
			selectedOptions: selectedOptions
		}, true, false);
	}

	private resetValidationScoreByStep = (event: CustomEvent): void => {
		const step = event.detail;

		this.resetValidationScore(step);
	}

	/**
	 * Listen for component events
	 * 1. Next step button disabling
	 * 2. Form submission
	 * 3. UK Switcher slide transitions
	 * 4. Add/Remove Slice to/from controlledForm
	 * 5. Form data changed
	 */
	private registerEvents = (): void => {

		window.addEventListener(OrganismFormStepperEvents.DISABLE_GO_TO_NEXT_STEP, this.disableNextStepButton);

		this.formComponent.addEventListener('submit', this.onSubmitForm);

		window.addEventListener(OrganismFormStepperEvents.RECEIVE_RESUME_SLICE, this.addResumeSliceToSummary)

		window.addEventListener(OrganismFormStepperEvents.RECEIVE_CONTROLLED_FORM_SLICE, this.addSliceToControlledForm)

		window.addEventListener(OrganismFormStepperEvents.REMOVE_CONTROLLED_FORM_SLICE, this.removeSliceFromControlledForm)

		window.addEventListener(OrganismFormStepperEvents.FORM_DATA_CHANGED_BY_STEP, this.formDataChangedByStep)

		window.addEventListener(OrganismFormStepperEvents.REQUEST_GO_TO_STEP, (event: CustomEvent) => {
			// @todo - check this...
			// this.setCurrentStep(event.detail)
			// this.goToStep()
			this.setRequestedStep(event.detail);
		})

		window.addEventListener(FormstepSelectImageEvents.SELECT_IMAGE, this.selectedImageOptionsChanged)

		window.addEventListener(OrganismFormStepperEvents.RESET_VALIDATION_SCORE_BY_STEP, this.resetValidationScoreByStep)

		this.nextStepButton.addEventListener('click', (e) => {
			// prevent button default action
			e.preventDefault();
			// this.goToStep('next')
		})
		this.prevStepButton.addEventListener('click', (e) => {
			e.preventDefault();
			// prevent button default action
			// this.goToStep('prev')
		})


		this.formComponent.addEventListener('submit', this.onSubmitForm);

		const sidebarItems = this.sidebar?.querySelectorAll('a')
		/**
		 * This listener in charge of keeping the sidebar and the state synchro
		 */
		if (sidebarItems) {
			sidebarItems.forEach((element) => {
				if (element) {
					element.addEventListener('click', (e) => {
						e.preventDefault()
						const element = e.target as HTMLAnchorElement
						const newCurrentStep = Number(element.dataset.step);
						// const { currentStep } = this.state.get();
						// const direction = newCurrentStep > currentStep ? 'next' : 'prev';
						this.setRequestedStep(newCurrentStep);
					});
				}
			})
		}

		this.topBackButton && this.topBackButton.addEventListener('click', (e) => {
			e.preventDefault()
			//TO-DO
		})

		this.topCloseButton && this.topCloseButton.addEventListener('click', (e) => {
			e.preventDefault()
			//TO-DO
		})

	}

	private disableNextStepButton = (e: CustomEvent<boolean>): void => {
		this.nextStepButton.disabled = e.detail
	}

	private updateIconsAndActiveSidebarItem(state: FormStepperState): void {
		if (!this.sidebar) return;

		const currentStep = state.currentStep;
		const newCheckedEl = document.getElementById('sourceCheckIcon')?.cloneNode(true).firstChild as HTMLSpanElement;
		const newEl = document.createElement('span');
		newEl.classList.add('uk-badge');

		Array.from(this.sidebar.querySelectorAll<'li'>('li')).forEach((link, index) => {

			if (link.classList.contains('uk-active') && currentStep !== index) {
				link.classList.remove('uk-active');
			}
			if (currentStep === index) {
				link.classList.add('uk-active');
			}

			const linkChild = link.firstChild;
			const idIndex = (index + 1).toString();
			const textEl = linkChild?.lastChild;

			if (linkChild && textEl) {
				const hasBadge = document.getElementById(idIndex);
				const hasBadgeChecked = document.getElementById(idIndex + '-checked');

				if (currentStep > index) {
					if (hasBadge && newCheckedEl) {
						newCheckedEl.setAttribute('id', idIndex + '-checked');
						linkChild.removeChild(hasBadge);
						linkChild.insertBefore(newCheckedEl, textEl);
					}
				} else {
					if (hasBadgeChecked) {
						const elContent = document.createTextNode(idIndex);
						newEl.setAttribute('id', idIndex);
						newEl.appendChild(elContent);
						linkChild.removeChild(hasBadgeChecked);
						linkChild.insertBefore(newEl, textEl);
						link.classList.remove('valid');
					}
				}
			}
		});
	}

	private showStep(currentStep: FormStepperState['currentStep']): void {
		if (!this.switcher) return;

		Array.from(this.switcher.querySelectorAll<'li'>('li')).forEach((page, index) => {
			if (currentStep !== index) {
				page.classList.remove('uk-active');
				page.classList.add('uk-hidden');
			} else {
				page.classList.remove('uk-hidden');
				page.classList.add('uk-active');
			}
		});
	}

	/**
	 * This method transform the data as needed in every situation
	 * @returns { formData: Record<string, any>}
	 */
	private formDataBuilder = (): Record<string, any> => {
		const formData: Record<string, any> = {}
		const { formType } = this.formConfig.settings
		const controlledData = this.state.get().controlledForm

		switch (formType) {
			case 'five-year-warranty':
				Object.keys(controlledData).forEach(key => {
					logs && console.warn(key.includes('system-component'), key)
					if (key.includes('system-component')) {
						if (!formData.systemComponents) formData.systemComponents = []
						formData.systemComponents.push(controlledData[key].formSlice)
					} else {
						formData[key] = controlledData[key].formSlice
					}
				})
				break;
			default:
				Object.keys(controlledData).forEach(key => formData[key] = controlledData[key].formSlice)
		}

		return formData
	}

	private renderAlert(message) {
		this.domUtils.renderDomElement(ConrollerFormSteps.componentRefs.submissionAlert, alertTemplate({
			data: {
				items: [
					{
						message,
						type: 'primary'
					}
				]

			}
		}))
	}

	private submitControlledForm(url: string): void {

		const formData = this.formDataBuilder()

		fetch(url.toString(), {
			method: 'POST',
			body: JSON.stringify(formData)
		}).then((resp) => {
			return resp.json();
		}).then((body: SubmitUserProfilingResponse) => {
			if (body.success === true) {
				const finalStep = this.state.get().totalSteps;
				this.setRequestedStep(finalStep);
				this.initDisableSidebarElements()
			} else {
				logs && console.warn('Form submission was not accepted by backend: ', body);
				this.renderAlert((body as SubmitUserProfilingResponseFailed).error)

			}
		}).catch((error) => {
			logs && console.warn(error);
		});
	}

	private submitFormData(event: SubmitEvent, url: string): void {

		const formData = new FormData(event.target as HTMLFormElement);

		fetch(url.toString(), {
			method: 'POST',
			body: formData
		}).then((resp) => {
			return resp.json();
		}).then((body: SubmitUserProfilingResponse) => {
			if (body.success === true) {
				const finalStep = this.state.get().totalSteps;
				this.setRequestedStep(finalStep);
				this.initDisableSidebarElements()
			} else {
				logs && console.log('Form submission was not accepted by backend: ', body);
				this.renderAlert((body as SubmitUserProfilingResponseFailed).error)

			}
		}).catch((error) => {
			logs && console.log(error);
		});
	}

	private onSubmitForm = (event: SubmitEvent): void => {
		event.preventDefault();
		const { controlledForm, action } = this.formConfig.settings
		// @todo - standardize this?
		if (controlledForm) this.submitControlledForm(action)
		else this.submitFormData(event, action)

	}

	private async validateStep<T>(url: URL, reqOptions: Partial<RequestInit> = {}): Promise<T> {
		// merge payload onto req
		const payload = Object.assign({
			method: 'POST'
		}, reqOptions);

		const resp = await fetch(url.toString(), payload);
		return await resp.json() as T;
	}

	private setFormConfig(): void {
		this.formConfig = this.domUtils.parseDOMScriptTag(FORM_STEPPER_CONFIG_ID);
	}

	private initDisableSidebarElements = (excpetions: number[] = []): void => {
		[...this.sidebar?.querySelectorAll('a') as NodeListOf<HTMLAnchorElement>].forEach((item, i) => !excpetions.includes[i] && item.setAttribute('style', 'pointer-events: none'))
	}

	/**
	 * Build validation configs for all steps and save to state
	 * @returns void
	 */
	private initValidationConfig(): void {
		const configs: FormStepValidationConfig[] = [];
		const initialValidation: FormStepperStateStepValidation[] = [];

		this.formConfig.steps.map((step, index) => {
			const { validation } = step || {};

			const validationEventType = validation && validation.eventType || null;
			const validationUrl = validation && validation.url || null;
			const validationEvent = validationEventType ? ValidationEventMapper[validationEventType] : null;
			const validationScoreRequired = this.getStepValidationScoreRequired(validationEvent, validationUrl);
			const config: FormStepValidationConfig = {
				validationEvent: validationEvent || '',
				validationUrl: validationUrl || '',
				validationScoreRequired,
			};
			configs.push(config);
			// set up initial validation states
			initialValidation.push({
				stepNumber: index,
				validationScoreCurrent: 0,
				validationScoreRequired,
			});
		});

		this.state.set(prevState => ({ ...prevState, validationConfig: configs, validatedSteps: initialValidation }));
	}

	/**
	 * Init the app
	 * @returns void
	 */
	private initApp(): void {
		this.setFormConfig()
		this.registerEvents()
		this.calculateTotalSteps()
		this.observeStepsHandler()
		this.initDisableSidebarElements([0])
		this.initValidationConfig()
		// this.initPrevalidatedField()
		this.showStep(0)
	}
}
